import React, { forwardRef, useState } from "react";
import { Formik, Form, Field, ErrorMessage, type FormikProps } from "formik";
import * as Yup from "yup";
import { Eye, EyeClosed } from "lucide-react";

interface Props {
    initialValues: any;
    onNext: (values: any) => void;
    onBack: (values?: any) => void;
}

const schema = Yup.object({
    firstname: Yup.string().required("First name required"),
    lastname: Yup.string().required("Last name required"),
    username: Yup.string().required("Username required"),
    othername: Yup.string().nullable(),
    email: Yup.string().email("Invalid email").required("Email required"),
    password: Yup.string()
        .required("Password required")
        .min(8, "Password must be at least 8 characters"),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref("password")], "Passwords must match")
        .required("Confirm password"),
    phone_number: Yup.string().required("Phone number required"),
});

const AdminForm: React.FC<Props> = forwardRef<FormikProps<any>, Props>(({ initialValues, onNext, onBack }, ref) => {
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    return (
        <Formik
            enableReinitialize
            innerRef={ref}
            initialValues={{
                firstname: initialValues.firstname || "",
                lastname: initialValues.lastname || "",
                username: initialValues.username || "",
                othername: initialValues.othername || "",
                email: initialValues.email || "",
                password: initialValues.password || "",
                confirmPassword: initialValues.confirmPassword || "",
                phone_number: initialValues.phone_number || "",
            }}
            validationSchema={schema}
            onSubmit={(values) => {
                onNext(values);
            }}
        >
            {({ isSubmitting }) => (
                <Form className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="form-label">First name</label>
                            <Field name="firstname" className="form-input" />
                            <ErrorMessage name="firstname" component="div" className="text-xs text-red-500 mt-1" />
                        </div>

                        <div>
                            <label className="form-label">Last name</label>
                            <Field name="lastname" className="form-input" />
                            <ErrorMessage name="lastname" component="div" className="text-xs text-red-500 mt-1" />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="form-label">Other name(s) <span className="text-gray-400 text-xs font-normal">(optional)</span></label>
                            <Field name="othername" className="form-input" />
                            <ErrorMessage name="othername" component="div" className="text-xs text-red-500 mt-1" />
                        </div>

                        <div>
                            <label className="form-label">Username</label>
                            <Field name="username" className="form-input" />
                            <ErrorMessage name="username" component="div" className="text-xs text-red-500 mt-1" />
                        </div>
                    </div>

                    <div>
                        <label className="form-label">Email</label>
                        <Field name="email" className="form-input" />
                        <ErrorMessage name="email" component="div" className="text-xs text-red-500 mt-1" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="relative mt-1">
                            <label className="form-label">Password</label>
                            <Field
                                name="password"
                                type={showPassword ? "text" : "password"}
                                className="form-input"
                            />
                            <ErrorMessage name="password" component="div" className="text-xs text-red-500 mt-1" />
                            <button
                                type="button"
                                onClick={() => setShowPassword((s) => !s)}
                                className="show-password"
                            >
                                {showPassword ? <EyeClosed /> : <Eye />}
                            </button>
                        </div>

                        <div className="relative mt-1">
                            <label className="form-label">Confirm Password</label>
                            <Field
                                name="confirmPassword"
                                type={showConfirmPassword ? "text" : "password"}
                                className="form-input"
                            />
                            <ErrorMessage name="confirmPassword" component="div" className="text-xs text-red-500 mt-1" />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword((s) => !s)}
                                className="show-password"
                            >
                                {showConfirmPassword ? <EyeClosed /> : <Eye />}
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="form-label">Phone Number</label>
                        <Field name="phone_number" className="form-input" />
                        <ErrorMessage name="phone_number" component="div" className="text-xs text-red-500 mt-1" />
                    </div>

                    <div className="flex justify-between">
                        <button
                            type="button"
                            onClick={() => onBack && onBack()}
                            className="px-4 py-2 rounded-md border border-gray-300 text-sm"
                        >
                            Back
                        </button>

                        <button
                            type="submit"
                            className="form-btn-mini"
                            disabled={isSubmitting}
                        >
                            Next
                        </button>
                    </div>
                </Form>
            )}
        </Formik>
    );
});

export default AdminForm;
